import React, { useRef } from 'react';
import PropTypes from 'prop-types';

import { useIsDelayed, useLiveProgressBarInfo } from '@dtvgo/player-utils-react';
import { ProgressBar as CommonProgressBar } from '@dtvgo/web-player-ui';

import ProgressBar from '../ProgressBar';
import { ClassNames } from '../../../utils';

import './LiveProgressBar.scss';

const LiveProgressBarClassNames = ClassNames.component('live-progress-bar');

export default function LiveProgressBar({
  className,
  gaps,
  extendedSeekEnabled = false,
  onSeekOutsideWindow,
  streamStartTimestamp = 0,
}) {
  const {
    progress, duration, seekable, handleSeek,
  } = useLiveProgressBarInfo();
  const delayed = useIsDelayed();

  // Ref avoids stale closure: Slider may hold a cached handleExtendedSeek
  // that was created before the ShakaPlayer ref provided seekToEpoch.
  const onSeekOutsideWindowRef = useRef(onSeekOutsideWindow);
  onSeekOutsideWindowRef.current = onSeekOutsideWindow;

  const realSeekableStart = seekable?.start ?? 0;
  const effectiveSeekable = extendedSeekEnabled
    ? { start: 0, end: seekable?.end }
    : seekable;

  const handleExtendedSeek = React.useCallback((value) => {
    if (
      extendedSeekEnabled
      && value < realSeekableStart
      && typeof onSeekOutsideWindowRef.current === 'function'
    ) {
      onSeekOutsideWindowRef.current(streamStartTimestamp + value);
    } else {
      handleSeek(value);
    }
  }, [extendedSeekEnabled, realSeekableStart, streamStartTimestamp, handleSeek]);

  return (
    <ProgressBar
      className={LiveProgressBarClassNames.block({ delayed }, className)}
      progress={progress}
      duration={duration}
      seekable={effectiveSeekable}
      gaps={gaps}
      onSeek={handleExtendedSeek}
    >
      <CommonProgressBar.SeekableEndDot className={LiveProgressBarClassNames.element('seekable-end-dot')} />
    </ProgressBar>
  );
}

LiveProgressBar.propTypes = {
  className: PropTypes.string,
  gaps: PropTypes.arrayOf(PropTypes.number),
  extendedSeekEnabled: PropTypes.bool,
  onSeekOutsideWindow: PropTypes.func,
  streamStartTimestamp: PropTypes.number,
};

LiveProgressBar.defaultProps = {
  className: '',
  gaps: undefined,
  extendedSeekEnabled: false,
  onSeekOutsideWindow: undefined,
  streamStartTimestamp: 0,
};
